'use client';

import React, { useState, useEffect } from 'react';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';
import { BookOpen, Search, Loader2, Star, ShoppingBag, Play, FileText, Package, Plus, ChevronRight } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { useAuth } from '@/contexts/AuthContext';

type DigitalProduct = {
  id: string;
  seller_id: string;
  title: string;
  description: string | null;
  short_description: string | null;
  price: number;
  currency: string;
  category: string;
  tags: string[];
  cover_image: string | null;
  cover_image_alt: string | null;
  is_featured: boolean;
  total_sales: number;
  seller?: {
    full_name: string | null;
  };
};

const CATEGORIES = [
  { value: 'all', label: 'All Products' },
  { value: 'course', label: 'Courses' },
  { value: 'video', label: 'Videos' },
  { value: 'guide', label: 'Guides & PDFs' },
  { value: 'resource', label: 'Resources' },
];

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  course: <BookOpen className="w-3.5 h-3.5" />,
  video: <Play className="w-3.5 h-3.5" />,
  guide: <FileText className="w-3.5 h-3.5" />,
  resource: <Package className="w-3.5 h-3.5" />,
};

function formatPrice(price: number, currency: string) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'gbp' }).format(price);
}

export default function ResourcesPage() {
  const { user } = useAuth();
  const [products, setProducts] = useState<DigitalProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  async function fetchProducts() {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('digital_products')
      .select('id, seller_id, title, description, short_description, price, currency, category, tags, cover_image, cover_image_alt, is_featured, total_sales')
      .eq('is_published', true)
      .order('is_featured', { ascending: false })
      .order('total_sales', { ascending: false });

    if (!error) {
      setProducts((data as DigitalProduct[]) || []);
    }
    setLoading(false);
  }

  const filtered = products.filter((p) => {
    const matchCat = activeCategory === 'all' || p.category === activeCategory;
    const matchSearch =
      !searchQuery ||
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.short_description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.tags?.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchCat && matchSearch;
  });

  const featured = filtered.filter((p) => p.is_featured);
  const rest = filtered.filter((p) => !p.is_featured);

  return (
    <div className="min-h-screen bg-[hsl(222,47%,6%)]">
      <Topbar />
      <main className="pt-16">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-12">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ShoppingBag className="w-5 h-5 text-amber-400" />
                <span className="text-xs font-semibold uppercase tracking-widest text-amber-400">Marketplace</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-3">Training Resources</h1>
              <p className="text-slate-400 text-sm max-w-xl leading-relaxed">
                Courses, video guides, and training resources from professional dog trainers. Buy once, access forever.
              </p>
            </div>
            {user && (
              <Link
                href="/resources/sell"
                className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm rounded-xl transition-all duration-150 whitespace-nowrap"
              >
                <Plus className="w-4 h-4" />
                Sell a Product
              </Link>
            )}
          </div>

          {/* Search + filters */}
          <div className="flex flex-col gap-3 mb-8">
            <div className="relative w-full sm:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="w-full bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] text-slate-100 placeholder-slate-500 rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-amber-500/60 transition-all"
              />
            </div>
            <div className="flex items-center gap-1 overflow-x-auto pb-1 -mx-1 px-1">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => setActiveCategory(cat.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg capitalize whitespace-nowrap transition-all flex-shrink-0 ${
                    activeCategory === cat.value
                      ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25' :'text-slate-400 hover:text-white border border-transparent hover:border-[hsl(222,25%,18%)]'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
            </div>
          ) : products.length === 0 ? (
            /* Empty state — no products posted yet */
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-20 h-20 rounded-2xl bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] flex items-center justify-center mx-auto mb-6">
                <ShoppingBag className="w-9 h-9 text-amber-400/60" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">No products yet</h2>
              <p className="text-slate-400 text-sm max-w-sm leading-relaxed mb-8">
                Be the first to list your dog training course, video guide, or resource pack. Products go live after a quick review.
              </p>
              {user ? (
                <Link
                  href="/resources/sell"
                  className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm rounded-xl transition-all duration-150"
                >
                  <Plus className="w-4 h-4" />
                  List Your Product
                </Link>
              ) : (
                <Link
                  href="/register"
                  className="flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm rounded-xl transition-all duration-150"
                >
                  Get Started <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <ShoppingBag className="w-10 h-10 text-slate-600 mb-3" />
              <p className="text-slate-400 font-medium mb-1">No products found</p>
              <p className="text-slate-600 text-sm">Try a different search or category</p>
            </div>
          ) : (
            <>
              {/* Featured products */}
              {featured.length > 0 && (
                <div className="mb-10">
                  <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Featured</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    {featured.map((product) => (
                      <ProductCard key={product.id} product={product} featured />
                    ))}
                  </div>
                </div>
              )}

              {/* All products grid */}
              {rest.length > 0 && (
                <div>
                  {featured.length > 0 && (
                    <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">All Products</h2>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {rest.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Sell CTA for non-logged-in users */}
          {!user && !loading && (
            <div className="mt-16 bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-2xl p-6 sm:p-8 text-center">
              <ShoppingBag className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-white mb-2">Are you a dog trainer?</h3>
              <p className="text-slate-400 text-sm mb-5 max-w-md mx-auto">
                Sell your courses, guides, and video content to the Driven Market community. Sign up free and start earning.
              </p>
              <Link
                href="/register"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm rounded-xl transition-all duration-150"
              >
                Get Started <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ProductCard({ product, featured = false }: { product: DigitalProduct; featured?: boolean }) {
  const categoryIcon = CATEGORY_ICONS[product.category] || <Package className="w-3.5 h-3.5" />;
  const categoryLabel = CATEGORIES.find((c) => c.value === product.category)?.label || product.category;

  if (featured) {
    return (
      <Link href={`/resources/${product.id}`} className="group block bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-2xl overflow-hidden hover:border-amber-500/30 transition-all duration-200">
        <div className="flex flex-col sm:flex-row">
          {product.cover_image && (
            <div className="w-full sm:w-48 aspect-[16/9] sm:aspect-auto overflow-hidden flex-shrink-0">
              <AppImage
                src={product.cover_image}
                alt={product.cover_image_alt || product.title}
                width={300}
                height={200}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          )}
          <div className="p-5 flex flex-col justify-between flex-1">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="flex items-center gap-1 text-xs font-semibold text-amber-400 capitalize">
                  {categoryIcon}{categoryLabel}
                </span>
                {product.is_featured && (
                  <span className="flex items-center gap-0.5 text-[10px] bg-amber-500/15 text-amber-400 border border-amber-500/25 px-1.5 py-0.5 rounded-md">
                    <Star className="w-2.5 h-2.5" /> Featured
                  </span>
                )}
              </div>
              <h3 className="text-base font-bold text-white mb-1.5 group-hover:text-amber-400 transition-colors leading-snug line-clamp-2">
                {product.title}
              </h3>
              {product.short_description && (
                <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 mb-3">{product.short_description}</p>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-white">
                {formatPrice(product.price, product.currency)}
              </span>
              {product.total_sales > 0 && (
                <span className="text-xs text-slate-600">{product.total_sales} sold</span>
              )}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link href={`/resources/${product.id}`} className="group block bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl overflow-hidden hover:border-amber-500/30 transition-all duration-200">
      {product.cover_image && (
        <div className="aspect-[16/9] overflow-hidden">
          <AppImage
            src={product.cover_image}
            alt={product.cover_image_alt || product.title}
            width={400}
            height={225}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-center gap-1.5 mb-2">
          <span className="flex items-center gap-1 text-xs font-semibold text-amber-400/80 capitalize">
            {categoryIcon}{categoryLabel}
          </span>
        </div>
        <h3 className="text-sm font-semibold text-white mb-1.5 group-hover:text-amber-400 transition-colors leading-snug line-clamp-2">
          {product.title}
        </h3>
        {product.short_description && (
          <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 mb-3">{product.short_description}</p>
        )}
        <div className="flex items-center justify-between pt-2 border-t border-[hsl(222,25%,18%)]">
          <span className="text-base font-bold text-white">{formatPrice(product.price, product.currency)}</span>
          {product.total_sales > 0 && (
            <span className="text-[10px] text-slate-600">{product.total_sales} sold</span>
          )}
        </div>
      </div>
    </Link>
  );
}
